import type { Distance, Margins, Pixel, Rectangle, SpatialRectangle } from "@pdfking/math"
import { BoundingBox } from "@pdfking/math"
import { ZERO_MARGINS } from "@pdfking/math/constants"
import { RecordMap } from "@pdfking/tfunc"

import type { LazyPage } from "#public"

// Fraction of the page reserved as breathing space on each non-touching side.
// Baked into the SVG (not a CSS margin on the wrapper) so the mask can trim it
// away exactly where two pages of a spread butt together.
export const PAGE_PADDING = 0.05

// Where a page sits in its scroll group: alone ("single"), or the left/right
// half of a spread — the half whose inner edge butts against its neighbour.
// One value instead of a clamp_left/clamp_right pair, so the illegal
// "clamped on both sides" state is unrepresentable and every caller derives it
// the one way (see SpreadSideAt) — measure (PlotViewer) and draw (PlotPage)
// can't disagree.
export type SpreadSide = "single" | "left" | "right"

// The single derivation of a page's spread side from its position in the group,
// shared by PlotGroup (draw) and PlotViewer (measure) so they never drift.
export const SpreadSideAt = (index: number, length: number): SpreadSide =>
  length <= 1 ? "single" : index === 0 ? "left" : index === length - 1 ? "right" : "single"

export type PageBoxFlags = {
  // Paint the bleed ring (else the paper is cropped to the trim box).
  bleed: boolean
  // Which side of a spread this page is — its inner edge drops its whole margin
  // (bleed + padding) so the trims meet with a hard edge and no gap.
  side: SpreadSide
}

export type PageBox = {
  // Element extent beyond the trim on each side. Bleed is reserved even while
  // the bleed toggle is off, so toggling it only repaints the paper (below) —
  // the element never resizes, so nothing re-fits or scrolls. Clamped sides
  // reserve nothing.
  margins: Margins<Pixel>
  // The painted rectangle (white paper + artwork) in page coordinates — the
  // mask both viewers clip to. Grows into the bleed only when `bleed` is on,
  // and stops hard at the trim on a clamped side.
  paper: SpatialRectangle<Pixel>
  // Full element size = trim + margins. Independent of the bleed toggle.
} & Rectangle<Pixel>

// Resolves a page's on-screen geometry from its trim size, bleed, and which
// sides are clamped by a neighbour. Shared by PlotPage (which draws it) and
// PlotViewer (which sizes the zoom fit and centring off it), so the two never
// drift.
export const ComputePageBox = (
  page: LazyPage,
  flags: PageBoxFlags,
  to_px: (value: Distance) => Pixel
): PageBox => {
  const trim_w = to_px(page.width)
  const trim_h = to_px(page.height)
  const bleed = RecordMap(to_px, page.bleed ?? ZERO_MARGINS)
  const pad_x = (trim_w * PAGE_PADDING) as Pixel
  const pad_y = (trim_h * PAGE_PADDING) as Pixel
  // A page's inner edge (against its spread neighbour) is clamped: the left half
  // clamps its right edge, the right half clamps its left.
  const clamp_left = flags.side === "right"
  const clamp_right = flags.side === "left"
  const margins: Margins<Pixel> = {
    bottom: (bleed.bottom + pad_y) as Pixel,
    left: (clamp_left ? 0 : bleed.left + pad_x) as Pixel,
    right: (clamp_right ? 0 : bleed.right + pad_x) as Pixel,
    top: (bleed.top + pad_y) as Pixel,
  }
  const left = (clamp_left ? 0 : flags.bleed ? -bleed.left : 0) as Pixel
  const right = (clamp_right ? trim_w : flags.bleed ? trim_w + bleed.right : trim_w) as Pixel
  // The paper is in the page's own space, y up from the trim's bottom-left.
  const bottom = (flags.bleed ? -bleed.bottom : 0) as Pixel
  const top = (flags.bleed ? trim_h + bleed.top : trim_h) as Pixel
  return {
    height: (trim_h + margins.top + margins.bottom) as Pixel,
    margins,
    paper: BoundingBox({ x: left, y: bottom }, { x: right, y: top }),
    width: (trim_w + margins.left + margins.right) as Pixel,
  }
}
